import type { DeepKeys } from '@tanstack/react-form'
import { zodValidator } from '@tanstack/zod-form-adapter'
import { cn } from 'lib/utils'
import { merge } from 'lodash'
import { Tooltip } from 'primitives/tooltip'
import React from 'react'
import type { ZodSchema } from 'zod'
import { componentTypeMap } from './component-map'
import { FieldInfo } from './field-info'
import { FieldMount } from './mount-field'
import type { FieldConfig, GenericFormType, ParentField, WithType } from './types'

const syncValidate = zodValidator()().validate

interface FieldComponentProps<T> {
  fieldConfig: FieldConfig<T>
  form: GenericFormType<T>
  arrayIndex?: number
  parentField?: ParentField<T>
  baseName?: string
}

export const FieldComponent = <T,>({
  fieldConfig,
  form,
  arrayIndex,
  parentField,
  baseName,
}: FieldComponentProps<T>) => {
  const Comp: React.ElementType | undefined =
    componentTypeMap[(fieldConfig as WithType<T>)?.componentType]?.component ||
    fieldConfig.component ||
    'div'
  const extractor =
    ((fieldConfig as FieldConfig<T>).eventDataExtractor as FieldConfig<T>['eventDataExtractor']) ||
    componentTypeMap[(fieldConfig as WithType<T>)?.componentType]?.extractValue

  const isArrayItem = arrayIndex !== undefined
  const [tick, setTick] = React.useState(true) // To control re-renders
  const lastName = fieldConfig.name.split('.').at(-1)
  const fieldName = isArrayItem
    ? `${baseName}${baseName && lastName ? '.' : ''}${lastName?.replace(/\d+/g, `${arrayIndex}`)}`
    : fieldConfig.name

  const isHidden = () =>
    typeof fieldConfig.visible === 'boolean'
      ? fieldConfig.visible === false
      : fieldConfig.visible?.(form.state.values, arrayIndex, baseName) === false

  const getValue = (value: unknown) => {
    if (fieldConfig.valueFormatter) {
      return fieldConfig.valueFormatter(value, arrayIndex)
    }
    return value
  }
  return (
    <form.Field
      name={fieldName as DeepKeys<T>}
      validators={{
        onBlur: fieldConfig.onBlurValidation,
        onChangeListenTo:
          typeof fieldConfig.onChangeListenTo === 'function'
            ? fieldConfig.onChangeListenTo(form.state.values, arrayIndex, baseName)
            : fieldConfig.onChangeListenTo,
        onChange: (value) => {
          setTick((t) => !t)
          if (isHidden()) return undefined
          const props = fieldConfig.getDynamicProps?.(form.state.values, arrayIndex, form, baseName)
          if (!props?.zodVal && fieldConfig.zodValidation instanceof Function) {
            const schema = fieldConfig.zodValidation(form.state.values, arrayIndex, baseName)
            if (schema === undefined || typeof schema === 'string') return schema
            return syncValidate(value, schema)
          }
          const schema = (props?.zodVal as ZodSchema) || fieldConfig.zodValidation
          if (!schema) return undefined
          return syncValidate(value, schema)
        },
      }}
    >
      {(field) => {
        if (isHidden()) return null
        const isDisabled = fieldConfig.disabled?.(form.state.values)
        const RawComponent = (
          <FieldMount
            Comp={Comp}
            props={{
              id: field.name,
              name: field.name,
              label: fieldConfig.label,
              arrayIndex,
              disabled: isDisabled,
              value: getValue(field.state.value),
              'data-tick': tick,
              form,
              onChange: (e: unknown) => {
                field.handleChange(extractor?.(e as never, form))
                fieldConfig.afterOnChange?.(form.state.values, arrayIndex, form, baseName)
                parentField?.validate('change')
              },
              baseName,
              onBlur: () => {
                field.validate('blur')
              },
              ...merge(
                {},
                fieldConfig.getDynamicProps?.(form.state.values, arrayIndex, form, baseName)
                  ?.inputProps,
                fieldConfig.inputProps,
              ),
            }}
          />
        )
        const component =
          typeof isDisabled === 'string' ? (
            <Tooltip content={isDisabled}>
              <div>{RawComponent}</div>
            </Tooltip>
          ) : (
            RawComponent
          )
        return (
          <div className={cn('col-span-full', fieldConfig.className)}>
            {component}
            <FieldInfo field={field} errorClassName={fieldConfig.errorClassName} />
          </div>
        )
      }}
    </form.Field>
  )
}
